import { useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { getIDFromUrl, Url } from '@rossum/api-client';
import { ColumnHeaderPaper } from '@rossum/components/data-grid/ColumnHeaderPaper';
import {
  commonDataGridStyles,
  getDatagridBackgroundStyles,
} from '@rossum/components/data-grid/styles';
import { DetailDrawer } from '@rossum/ui';
import { IconTrash } from '@rossum/ui/icons/tabler';
import { Box, CircularProgress, Paper, Stack } from '@rossum/ui/material';
import {
  DataGridPro,
  gridClasses,
  GridProSlotsComponent,
} from '@rossum/ui/x-data-grid-pro';
import { drawerDataGridStyles } from '../../document-list-base/drawerDataGridStyles';
import { SupportedAnnotationView } from '../../document-list-base/supportedAnnotationViews';
import { CustomRow } from '../../document-list-columns/components/CustomRow';
import {
  ACTIONS_COLUMN,
  useSimpleColumns,
} from '../../document-list-columns/hooks/useSimpleColumns';
import { useSimpleColumnWidths } from '../../document-list-columns/hooks/useSimpleColumnWidths';
import { UnpaidFeatureOverlay } from '../../pricing/components/UnpaidFeatureOverlay';
import { duplicateHandlingFeatureSelector } from '../../pricing/selectors';
import { useCanRemoveDuplicates } from './hooks/useCanRemoveDuplicates';
import { useDeleteAnnotations } from './hooks/useDeleteAnnotations';
import {
  DUPLICATES_QUERY_KEY,
  useDocumentDuplicates,
} from './hooks/useDocumentDuplicates';
import { NoDuplicatesFound } from './NoDuplicatesFound';

type HandleSelectAnnotation = (params: {
  annotationUrl: string;
  view: SupportedAnnotationView;
}) => void;

export type UseDuplicatesDrawerState = {
  parentAnnotationUrl: Url | null;
};

type OwnProps = {
  onClose: () => void;
  handleSelectAnnotation: HandleSelectAnnotation;
  onAnnotationDelete?: (annotation: Url) => void;
};

type DuplicatesDrawerProps = UseDuplicatesDrawerState & OwnProps;

export const DuplicatesDrawer = ({
  onClose,
  parentAnnotationUrl,
  handleSelectAnnotation,
  onAnnotationDelete,
}: DuplicatesDrawerProps) => {
  const intl = useIntl();

  const isDuplicateFeaturePurchased = useSelector(
    duplicateHandlingFeatureSelector
  );

  return (
    <DetailDrawer
      open={!!parentAnnotationUrl}
      title={intl.formatMessage({
        id: 'components.duplicatesDrawer.title',
      })}
      onClose={onClose}
      size="large"
    >
      {!isDuplicateFeaturePurchased ? (
        <UnpaidFeatureOverlay
          title={intl.formatMessage({
            id: 'features.pricing.unpaidFeatureOverlay.title.duplicates',
          })}
          dataCy="unpaid-overlay-duplicates-drawer"
        />
      ) : (
        parentAnnotationUrl && (
          <DuplicatesDrawerContent
            onAnnotationDelete={onAnnotationDelete}
            parentAnnotationUrl={parentAnnotationUrl}
            onClose={onClose}
            handleSelectAnnotation={handleSelectAnnotation}
          />
        )
      )}
    </DetailDrawer>
  );
};

const DuplicatesDrawerContent = ({
  onClose: _,
  parentAnnotationUrl,
  handleSelectAnnotation,
  onAnnotationDelete,
}: OwnProps & { parentAnnotationUrl: Url }) => {
  const { duplicates, isLoading, isError } =
    useDocumentDuplicates(parentAnnotationUrl);

  const canPerformDuplicatesActions = useCanRemoveDuplicates();

  const queryClient = useQueryClient();
  const { mutate: deleteAnnotations } = useDeleteAnnotations();

  const onDuplicateDelete = (duplicateUrl: Url, parentUrl: Url) => {
    deleteAnnotations([getIDFromUrl(duplicateUrl)], {
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: [DUPLICATES_QUERY_KEY, parentUrl],
        });
      },
      onSuccess: () => {
        onAnnotationDelete?.(duplicateUrl);
      },
    });
  };

  const { columnWidths, onColumnWidthChange } =
    useSimpleColumnWidths('duplicatesDrawer');

  const columns = useSimpleColumns({
    removeProps: canPerformDuplicatesActions
      ? {
          dataCyPrefix: 'duplicate',
          onRemove: duplicateUrl =>
            onDuplicateDelete(duplicateUrl, parentAnnotationUrl),
          Icon: IconTrash,
          disableButton: annotation => annotation.status === 'deleted',
        }
      : undefined,
    detailsColumnProps: { handleSelectAnnotation, annotations: duplicates },
    columnsLocation: 'duplicatesDrawer',
    columnWidths,
  });

  return (
    <Stack p={3}>
      <Stack height={1}>
        <DataGridPro
          disableRowSelectionExcludeModel
          loading={isLoading}
          autoHeight
          columns={columns}
          rows={!isError ? duplicates : []}
          disableColumnMenu
          disableRowSelectionOnClick
          hideFooterRowCount
          onColumnWidthChange={onColumnWidthChange}
          pinnedColumns={{
            right: [ACTIONS_COLUMN],
          }}
          sx={{
            ...getDatagridBackgroundStyles('paper'),
            ...drawerDataGridStyles,
            ...commonDataGridStyles,
            [`& .${gridClasses.virtualScrollerContent}`]: {
              height: isLoading ? '720px !important' : 'auto',
              maxHeight: isLoading ? '250px' : '64vh',
            },
            [`& .${gridClasses.virtualScroller}`]: {
              overflow: `${isLoading ? 'hidden' : 'auto'} !important`,
            },
          }}
          slots={{
            columnHeaders: ColumnHeaderPaper,
            row: CustomRow as GridProSlotsComponent['row'], // GridProSlotsComponent doesn't accept generics https://v7.mui.com/x/common-concepts/custom-components/#using-additional-props
            noRowsOverlay: NoDuplicatesFound,
            noResultsOverlay: NoDuplicatesFound,
            loadingOverlay: () => (
              <Stack
                width={1}
                height={1}
                justifyContent="center"
                alignItems="center"
                position="relative"
              >
                <Box
                  width={1}
                  height={1}
                  component={Paper}
                  elevation={2}
                  position="absolute"
                  sx={{ opacity: 0.8 }}
                />
                <CircularProgress />
              </Stack>
            ),
          }}
          slotProps={{
            columnHeaders: {
              // @ts-expect-error
              elevation: 0,
            },
          }}
        />
      </Stack>
    </Stack>
  );
};
